import { useCallback, useEffect, useState } from "react";
import {
  acknowledgeMaterialSdsOpen,
  generateMaterialSdsDraft,
  getMaterialSdsDetail,
  publishMaterialSds,
  saveMaterialSdsDraft,
  uploadMaterialSdsSupplierPdf,
} from "@/lib/manufacturing/materialsMsdsClient";
import type {
  MfgMaterialSdsDocument,
  MsdsGenerateHints,
} from "@/lib/manufacturing/msdsDocumentTypes";
import {
  emptySdsSections,
  normalizeSdsSections,
  type MfgSdsSectionKey,
  type MfgSdsSections,
} from "@/lib/manufacturing/msdsSections";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import { renderSdsPdfToBlob } from "@/components/manufacturing/pdf/generateSdsPdf";
import { SDS_EDIT_AFTER_PUBLISH_CONFIRM } from "@/components/manufacturing/pdf/sdsPdfFooterCopy";

type Args = {
  open: boolean;
  enabled: boolean;
  material: MfgMaterial | null;
};

function needsFirstOpenAck(doc: MfgMaterialSdsDocument | null): boolean {
  return !!doc && doc.source === "ai_draft" && !doc.first_opened_by_user_id;
}

function isConfirmRedraftError(r: { status: number; body?: unknown }): boolean {
  if (r.status !== 409) return false;
  const body = r.body;
  return (
    !!body &&
    typeof body === "object" &&
    (body as { code?: string }).code === "confirm_redraft_required"
  );
}

export function useManufacturingMaterialSds(args: Args) {
  const { open, enabled, material } = args;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sdsDocument, setSdsDocument] = useState<MfgMaterialSdsDocument | null>(null);
  const [publishedDocument, setPublishedDocument] = useState<MfgMaterialSdsDocument | null>(
    null
  );
  const [sections, setSections] = useState<Record<MfgSdsSectionKey, string>>(emptySdsSections());
  const [dirty, setDirty] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [signOffChecked, setSignOffChecked] = useState(false);

  const applyDocs = useCallback(
    (draft: MfgMaterialSdsDocument | null, published: MfgMaterialSdsDocument | null) => {
      setSdsDocument(draft);
      setPublishedDocument(published);
      const active = draft ?? published;
      setSections(normalizeSdsSections(active?.sections ?? {}));
      setDirty(false);
      setAckOpen(needsFirstOpenAck(draft));
      setSignOffChecked(false);
    },
    []
  );

  const reset = useCallback(() => {
    setLoading(false);
    setSaving(false);
    setGenerating(false);
    setExporting(false);
    setAcknowledging(false);
    setPublishing(false);
    setUploading(false);
    setError(null);
    setSdsDocument(null);
    setPublishedDocument(null);
    setSections(emptySdsSections());
    setDirty(false);
    setAckOpen(false);
    setSignOffChecked(false);
  }, []);

  useEffect(() => {
    if (!open || !enabled || !material) {
      reset();
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const r = await getMaterialSdsDetail(material.id, material.plant_id);
      if (cancelled) return;
      if (!r.ok) {
        setError(r.error || "Failed to load SDS");
        setLoading(false);
        return;
      }
      applyDocs(r.data.draft, r.data.published);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, enabled, material, reset, applyDocs]);

  const setSection = useCallback((key: MfgSdsSectionKey, value: string) => {
    setSections((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }, []);

  const save = useCallback(
    async (opts?: { confirmRedraft?: boolean }) => {
      if (!material) return { ok: false as const, error: "No material" };
      if (!opts?.confirmRedraft && !sdsDocument && publishedDocument) {
        const ok = window.confirm(SDS_EDIT_AFTER_PUBLISH_CONFIRM);
        if (!ok) return { ok: false as const, error: "Cancelled" };
        opts = { confirmRedraft: true };
      }
      setSaving(true);
      setError(null);
      const payloadSections: MfgSdsSections = { ...sections };
      const r = await saveMaterialSdsDraft({
        material_id: material.id,
        plant_id: material.plant_id,
        sections: payloadSections,
        confirm_redraft: opts?.confirmRedraft === true,
      });
      setSaving(false);
      if (!r.ok) {
        if (isConfirmRedraftError(r)) {
          const ok = window.confirm(SDS_EDIT_AFTER_PUBLISH_CONFIRM);
          if (ok) return save({ confirmRedraft: true });
          return { ok: false as const, error: "Cancelled" };
        }
        setError(r.error || "Failed to save SDS draft");
        return { ok: false as const, error: r.error };
      }
      setSdsDocument(r.data.document);
      if (r.data.superseded) setPublishedDocument(null);
      setSections(normalizeSdsSections(r.data.document.sections));
      setDirty(false);
      return { ok: true as const };
    },
    [material, sections, sdsDocument, publishedDocument]
  );

  const generate = useCallback(
    async (hints: MsdsGenerateHints) => {
      if (!material) return { ok: false as const, error: "No material" };
      setGenerating(true);
      setError(null);
      const r = await generateMaterialSdsDraft({
        material_id: material.id,
        plant_id: material.plant_id,
        hints,
      });
      setGenerating(false);
      if (!r.ok) {
        setError(r.error || "Failed to generate SDS draft");
        return { ok: false as const, error: r.error };
      }
      setSdsDocument(r.data.document);
      setSections(normalizeSdsSections(r.data.document.sections));
      setDirty(false);
      setAckOpen(needsFirstOpenAck(r.data.document));
      return { ok: true as const };
    },
    [material]
  );

  const acknowledgeOpen = useCallback(async () => {
    if (!material || !sdsDocument) return { ok: false as const, error: "No draft" };
    setAcknowledging(true);
    setError(null);
    const r = await acknowledgeMaterialSdsOpen({
      material_id: material.id,
      document_id: sdsDocument.id,
    });
    setAcknowledging(false);
    if (!r.ok) {
      setError(r.error || "Failed to acknowledge open");
      return { ok: false as const, error: r.error };
    }
    setSdsDocument(r.data.document);
    setAckOpen(false);
    return { ok: true as const };
  }, [material, sdsDocument]);

  const publish = useCallback(async () => {
    if (!material || !sdsDocument) return { ok: false as const, error: "No draft" };
    if (!signOffChecked) {
      setError("Check the review sign-off box before publishing.");
      return { ok: false as const, error: "Sign-off required" };
    }
    setPublishing(true);
    setError(null);
    const r = await publishMaterialSds({
      material_id: material.id,
      document_id: sdsDocument.id,
      plant_id: material.plant_id,
      review_sign_off: true,
    });
    setPublishing(false);
    if (!r.ok) {
      setError(r.error || "Failed to publish SDS");
      return { ok: false as const, error: r.error };
    }
    setSdsDocument(null);
    setPublishedDocument(r.data.document);
    setSections(normalizeSdsSections(r.data.document.sections));
    setDirty(false);
    setSignOffChecked(false);
    return { ok: true as const };
  }, [material, sdsDocument, signOffChecked]);

  const uploadSupplier = useCallback(
    async (file: File) => {
      if (!material) return { ok: false as const, error: "No material" };
      setUploading(true);
      setError(null);
      const r = await uploadMaterialSdsSupplierPdf({ materialId: material.id, file });
      setUploading(false);
      if (!r.ok) {
        setError(r.error || "Failed to upload supplier SDS");
        return { ok: false as const, error: r.error };
      }
      setSdsDocument(r.document);
      setSections(normalizeSdsSections(r.document.sections));
      setDirty(false);
      setSignOffChecked(false);
      return { ok: true as const };
    },
    [material]
  );

  const exportPdf = useCallback(async () => {
    if (!material) return { ok: false as const, error: "No material" };
    const active = sdsDocument ?? publishedDocument;
    setExporting(true);
    setError(null);
    const result = await renderSdsPdfToBlob({
      materialCode: material.code,
      materialName: material.name,
      sections,
      footerState: active?.footer_state ?? "ai_draft",
      source: active?.source ?? "manual",
      generatedAt: active?.ai_generated_at,
      generatedByUserId: active?.ai_generated_by_user_id,
      promptVersion: active?.ai_prompt_version,
      sourceInputsHash: active?.source_inputs_hash,
      reviewerDisplayName: active?.review_signed_off_display_name,
      reviewedAt: active?.review_signed_off_at,
    });
    setExporting(false);
    if (!result.ok) {
      setError(result.error);
      return result;
    }
    const url = URL.createObjectURL(result.blob);
    const a = window.document.createElement("a");
    a.href = url;
    const tag = active?.status === "published" ? "published" : "draft";
    a.download = `SDS-${material.code || material.id}-${tag}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
    return { ok: true as const };
  }, [material, sections, sdsDocument, publishedDocument]);

  return {
    loading,
    saving,
    generating,
    exporting,
    acknowledging,
    publishing,
    uploading,
    error,
    sdsDocument,
    publishedDocument,
    sections,
    dirty,
    ackOpen,
    signOffChecked,
    setSignOffChecked,
    setSection,
    save,
    generate,
    acknowledgeOpen,
    publish,
    uploadSupplier,
    exportPdf,
    reset,
  };
}
